import "./AutomatedOffersPage.scss";
import React, { useState } from "react";
import AutomatedOffersHeader from "./AutomatedOffersHeader";
import AutomatedOffersFooter from "./AutomatedOffersFooter";
import OfferStack from "./OfferStack";
import AutomatedOffersForm from "../../components/form/AutomatedOffersForm";
import AutomatedOffersSubmitForm from "../../components/form/AutomatedOffersSubmitForm";
import brand from "./brands";

// The main site shares one nav across its pages; the standalone site keeps its own header.
// REACT_APP_SITE is inlined at build time, so the standalone build doesn't bundle the shared nav.
const SiteHeader =
	process.env.REACT_APP_SITE === "automated-offers"
		? null
		: require("../../components/ui/StickyHeader").default;

const EnrollForm =
	brand.form.variant === "singleSubmit" ? AutomatedOffersSubmitForm : AutomatedOffersForm;

const { hero, how, included, packagesSection, timelineSection, enroll } = brand;

function IncludedPanels() {
	return (
		<div className="included">
			{included.groups.map((group) => (
				<article key={group.title} className="inc-group">
					<header className="inc-group__intro">
						<h3 className="inc-group__title">{group.title}</h3>
						<hr className="inc-group__rule" />
					</header>
					<ul className="inc-group__items">
						{group.items.map((item) => (
							<li className="inc" key={item.title}>
								<h4>{item.title}</h4>
								<p>{item.body}</p>
								{item.timing && <span className="inc__badge">{item.timing}</span>}
							</li>
						))}
					</ul>
				</article>
			))}
		</div>
	);
}

function scrollToSection(id) {
	const behavior = window.matchMedia("(prefers-reduced-motion: reduce)").matches
		? "auto"
		: "smooth";
	if (id === "top") {
		window.scrollTo({ top: 0, behavior });
		return;
	}
	document.getElementById(id)?.scrollIntoView({ behavior, block: "start" });
}

/** In-page links scroll to their section without adding the #hash to the URL. */
function handleInPageLink(e) {
	const link = e.target.closest('a[href^="#"]');
	const id = link?.getAttribute("href").slice(1);
	if (!id || !document.getElementById(id)) return;
	e.preventDefault();
	scrollToSection(id);
}

const AutomatedOffersPage = () => {
	const [selection, setSelection] = useState(brand.form.packagePicks.single);

	React.useEffect(() => {
		window.scrollTo(0, 0);
		// Also served at /automated-offers on the main site, whose index.html title differs.
		const previousTitle = document.title;
		document.title = brand.pageTitle;
		return () => {
			document.title = previousTitle;
		};
	}, []);

	const handlePickPackage = (pick) => {
		setSelection(brand.form.packagePicks[pick]);
		scrollToSection("enroll");
	};

	const heroCopy = (
		<div className="hero-copy">
			{hero.hat && <p className="hat">{hero.hat}</p>}
			<h1 className="hero-title">
				{hero.titleLines.map((line, i) => (
					<React.Fragment key={line}>
						{i > 0 && <br />}
						<span className={`hero-title__line hero-title__line--${i + 1}`}>{line}</span>
					</React.Fragment>
				))}
			</h1>
			<p className="lead">{hero.lead}</p>
			<div className="hero-ctas">
				<a className="btn" href="#enroll">
					{hero.primaryCta}
				</a>
				<a className="btn ghost" href="#packages">
					{hero.secondaryCta}
				</a>
			</div>
		</div>
	);

	return (
		<div
			className={`automated-offers-page brand-${brand.id}${SiteHeader ? " automated-offers-page--site-nav" : ""}`}
			onClick={handleInPageLink}>
			{SiteHeader ? (
				<SiteHeader enrollScrollTargetId="enroll" />
			) : (
				<AutomatedOffersHeader />
			)}

			<main id="top">
				<section className="hero">
					<div className="wrap">
						<div className="hero-panel">
							{heroCopy}
							<figure className="hero-figure">
								<picture>
									<source media="(max-width: 900px)" srcSet={hero.imageMobile} />
									<img src={hero.image} alt={hero.imageAlt} className="hero-image" />
								</picture>
							</figure>
						</div>
						{hero.caption && <p className="tile-cap">{hero.caption}</p>}
					</div>
				</section>

				{brand.statement.length > 0 && (
					<section className="statement" aria-label="Program summary">
						<div className="wrap">
							{brand.statement.map((note) => (
								<p key={note}>{note}</p>
							))}
						</div>
					</section>
				)}

				<section className="block block--alt" id="how">
					<div className="wrap">
						<div className="section-head">
							<h2>{how.title}</h2>
							<p className="sub">{how.sub}</p>
						</div>
						<div className="steps">
							{how.steps.map((s) => (
								<div className="step" key={s.step}>
									<div className="n">{s.step}</div>
									<h3>{s.title}</h3>
									<p>{s.body}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="block" id="included">
					<div className="wrap">
						<div className="section-head">
							<h2>{included.title}</h2>
							<p className="sub">{included.sub}</p>
						</div>
						{included.collage && (
							<img
								src={included.collage.image}
								alt={included.collage.imageAlt}
								className={`included-collage${included.stack ? " included-collage--touch" : ""}`}
								loading="lazy"
							/>
						)}
						{included.stack && (
							<OfferStack
								cards={included.stack.cards}
								dealerLabel={included.stack.dealerLabel}
								cta={included.stack.cta}
								label={included.collage.imageAlt}
							/>
						)}
						<IncludedPanels />
						{included.note && <p className="included-note">{included.note}</p>}
					</div>
				</section>

				<section className="block block--alt" id="packages">
					<div className="wrap">
						<div className="section-head">
							<h2>{packagesSection.title}</h2>
							<p className="sub">{packagesSection.sub}</p>
						</div>
						<div className="packages">
							{packagesSection.items.map((pkg) => (
								<div className="pkg" key={pkg.pick}>
									<p className="who">{pkg.who}</p>
									<div className="amt">{pkg.amount}</div>
									<div className="per">{pkg.per}</div>
									<ul>
										{pkg.features.map((feature) => (
											<li key={feature}>{feature}</li>
										))}
									</ul>
									<button
										type="button"
										className="btn pick"
										onClick={() => handlePickPackage(pkg.pick)}>
										{pkg.cta}
									</button>
								</div>
							))}
						</div>

						{packagesSection.volume && (
							<div className="volume">
								<h3>{packagesSection.volume.title}</h3>
								<p>{packagesSection.volume.body}</p>
								<p className="volume__note">{packagesSection.volume.note}</p>
								<a
									className="btn ghost volume__cta"
									href={packagesSection.volume.ctaHref}
									target="_blank"
									rel="noopener noreferrer">
									{packagesSection.volume.cta}
								</a>
							</div>
						)}
					</div>
				</section>

				<section className="block">
					<div className="wrap">
						<div className="section-head">
							<h2>{timelineSection.title}</h2>
							<p className="sub">{timelineSection.sub}</p>
						</div>
						<div className="timeline">
							{timelineSection.items.map((t) => (
								<div className="tl" key={t.title}>
									<div className="when">{t.when}</div>
									<h3>{t.title}</h3>
									<p>{t.body}</p>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="block block--alt enroll" id="enroll">
					<div className="wrap">
						<div className="section-head">
							<h2>{enroll.title}</h2>
							<p className="sub">{enroll.sub}</p>
						</div>
						<EnrollForm selection={selection} onSelectionChange={setSelection} />
					</div>
				</section>
			</main>

			<AutomatedOffersFooter />
		</div>
	);
};

export default AutomatedOffersPage;
